import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { getDocument } from '../utils/matchHelpers';
import Footer from '../components/Footer';

export default function Leaderboard() {
  const [players, setPlayers] = useState([]);
  const [tournaments, setTournaments] = useState([]);
  const [teamStandings, setTeamStandings] = useState([]);
  const [selectedTournament, setSelectedTournament] = useState('');
  const [tab, setTab] = useState('players');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getDocs(collection(db, 'players')),
      getDocs(collection(db, 'tournaments')),
    ]).then(([pSnap, tSnap]) => {
      setPlayers(
        pSnap.docs.map(d => ({ id: d.id, ...d.data() }))
          .sort((a, b) => b.wins - a.wins || (b.points_scored - b.points_against) - (a.points_scored - a.points_against))
      );
      setTournaments(
        tSnap.docs.map(d => ({ id: d.id, ...d.data() })).filter(t => t.status !== 'setup')
      );
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedTournament) return;
    (async () => {
      const teamsSnap = await getDocs(query(collection(db, 'teams'), where('tournament_id', '==', selectedTournament)));
      const teams = teamsSnap.docs.map(d => ({ id: d.id, ...d.data() }));

      const playerIds = [...new Set(teams.flatMap(t => [t.player1_id, t.player2_id].filter(Boolean)))];
      const playerMap = {};
      await Promise.all(playerIds.map(async pid => {
        const p = await getDocument('players', pid);
        if (p) playerMap[pid] = p;
      }));

      const result = teams.map(t => ({
        ...t,
        player1_name: playerMap[t.player1_id]?.name || null,
        player2_name: playerMap[t.player2_id]?.name || null,
        win_pct: (t.wins + t.losses) > 0 ? Math.round(t.wins / (t.wins + t.losses) * 1000) / 10 : 0,
      })).sort((a, b) => b.wins - a.wins || (b.points_for - b.points_against) - (a.points_for - a.points_against));

      setTeamStandings(result);
    })();
  }, [selectedTournament]);

  if (loading) return <div style={{ padding: '60px', textAlign: 'center' }}>Loading...</div>;

  const medals = ['🥇', '🥈', '🥉'];

  return (
    <div className="container" style={{ padding: '32px 20px' }}>
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '800' }}>Leaderboard</h1>
        <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '4px' }}>Player rankings and tournament standings</p>
      </div>

      <div style={{ display: 'flex', gap: '4px', background: '#f0f4f8', borderRadius: '8px', padding: '4px', marginBottom: '24px', width: 'fit-content' }}>
        {[{ key: 'players', label: '👤 Players' }, { key: 'teams', label: '🏆 Tournament' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{ padding: '8px 20px', borderRadius: '6px', fontSize: '14px', fontWeight: '600', background: tab === t.key ? '#fff' : 'transparent', color: tab === t.key ? '#27ae60' : '#7f8c8d', boxShadow: tab === t.key ? '0 2px 8px rgba(0,0,0,0.08)' : 'none', border: 'none', cursor: 'pointer' }}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'players' && (
        <div className="animate-fadein">
          {players.length >= 3 && (
            <div style={{ display: 'flex', justifyContent: 'center', gap: '16px', marginBottom: '32px', alignItems: 'flex-end' }}>
              {[1, 0, 2].map(rank => {
                const p = players[rank];
                if (!p) return null;
                const heights = [160, 130, 110];
                const colors = ['#FFD700', '#C0C0C0', '#CD7F32'];
                let hue = 0; for (let i = 0; i < p.id.length; i++) hue = (hue * 31 + p.id.charCodeAt(i)) % 360;
                return (
                  <div key={rank} style={{ textAlign: 'center', animation: 'slideUp 0.4s ease' }}>
                    <div style={{ fontSize: rank === 0 ? '20px' : '16px' }}>{medals[rank]}</div>
                    <div style={{ width: rank === 0 ? '90px' : '72px', height: heights[rank], background: `linear-gradient(180deg,${colors[rank]}22,${colors[rank]}44)`, border: `2px solid ${colors[rank]}66`, borderRadius: '8px 8px 0 0', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'flex-end', paddingBottom: '12px', margin: '4px auto 0' }}>
                      <div style={{ width: rank === 0 ? '44px' : '36px', height: rank === 0 ? '44px' : '36px', borderRadius: '50%', background: `hsl(${hue},60%,80%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: rank === 0 ? '18px' : '14px', color: `hsl(${hue},60%,35%)`, marginBottom: '6px' }}>
                        {p.name.charAt(0)}
                      </div>
                      <div style={{ fontSize: rank === 0 ? '13px' : '11px', fontWeight: '700', color: '#2c3e50' }}>{p.name}</div>
                      <div style={{ fontSize: '11px', color: '#7f8c8d' }}>{p.wins}W · {p.losses}L</div>
                    </div>
                    <div style={{ background: colors[rank], color: '#fff', padding: '4px 8px', borderRadius: '0 0 6px 6px', fontSize: '13px', fontWeight: '800', width: rank === 0 ? '90px' : '72px', margin: '0 auto' }}>#{rank + 1}</div>
                  </div>
                );
              })}
            </div>
          )}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Rank</th><th>Player</th>
                  <th style={{ textAlign: 'center' }}>W</th><th style={{ textAlign: 'center' }}>L</th>
                  <th style={{ textAlign: 'center' }}>Win %</th><th style={{ textAlign: 'center' }}>Pts Scored</th>
                  <th style={{ textAlign: 'center' }}>Pts Against</th><th style={{ textAlign: 'center' }}>+/-</th>
                  <th style={{ textAlign: 'center' }}>Matches</th>
                </tr>
              </thead>
              <tbody>
                {players.map((p, i) => {
                  const total = p.wins + p.losses;
                  const winPct = total > 0 ? Math.round((p.wins / total) * 100) : 0;
                  const diff = p.points_scored - p.points_against;
                  let hue = 0; for (let j = 0; j < p.id.length; j++) hue = (hue * 31 + p.id.charCodeAt(j)) % 360;
                  return (
                    <tr key={p.id} className="animate-fadein" style={{ animationDelay: `${i * 0.03}s` }}>
                      <td><span style={{ fontWeight: '700', fontSize: '15px' }}>{i < 3 ? medals[i] : <span style={{ color: '#7f8c8d' }}>#{i + 1}</span>}</span></td>
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: `hsl(${hue},60%,85%)`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: '700', fontSize: '13px', color: `hsl(${hue},60%,35%)` }}>
                            {p.name.charAt(0)}
                          </div>
                          <span style={{ fontWeight: '600' }}>{p.name}</span>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', color: '#27ae60', fontWeight: '700' }}>{p.wins}</td>
                      <td style={{ textAlign: 'center', color: '#e74c3c', fontWeight: '700' }}>{p.losses}</td>
                      <td style={{ textAlign: 'center' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px' }}>
                          <span style={{ fontWeight: '700', color: winPct >= 60 ? '#27ae60' : winPct >= 40 ? '#f39c12' : '#e74c3c' }}>{winPct}%</span>
                          <div style={{ width: '48px', height: '4px', background: '#f0f4f8', borderRadius: '2px', overflow: 'hidden' }}>
                            <div style={{ width: `${winPct}%`, height: '100%', background: winPct >= 60 ? '#27ae60' : winPct >= 40 ? '#f39c12' : '#e74c3c', borderRadius: '2px' }} />
                          </div>
                        </div>
                      </td>
                      <td style={{ textAlign: 'center', fontWeight: '600' }}>{p.points_scored}</td>
                      <td style={{ textAlign: 'center', color: '#7f8c8d' }}>{p.points_against}</td>
                      <td style={{ textAlign: 'center', fontWeight: '700', color: diff >= 0 ? '#27ae60' : '#e74c3c' }}>{diff >= 0 ? '+' : ''}{diff}</td>
                      <td style={{ textAlign: 'center', color: '#7f8c8d' }}>{p.matches_played}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {players.length === 0 && <div className="empty-state"><div className="icon">📊</div><h3>No player stats yet</h3><p>Stats will appear after matches are played</p></div>}
          </div>
        </div>
      )}

      {tab === 'teams' && (
        <div className="animate-fadein">
          <div className="card" style={{ marginBottom: '20px', padding: '16px 20px' }}>
            <div className="form-group" style={{ marginBottom: 0 }}>
              <label>Select Tournament</label>
              <select className="form-control" value={selectedTournament} onChange={e => setSelectedTournament(e.target.value)} style={{ maxWidth: '320px' }}>
                <option value="">Choose a tournament...</option>
                {tournaments.map(t => <option key={t.id} value={t.id}>{t.name} ({t.type})</option>)}
              </select>
            </div>
          </div>

          {selectedTournament && (
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>Rank</th><th>Team</th>
                    <th style={{ textAlign: 'center' }}>W</th><th style={{ textAlign: 'center' }}>L</th>
                    <th style={{ textAlign: 'center' }}>Win %</th><th style={{ textAlign: 'center' }}>Pts For</th>
                    <th style={{ textAlign: 'center' }}>Pts Against</th><th style={{ textAlign: 'center' }}>Diff</th>
                  </tr>
                </thead>
                <tbody>
                  {teamStandings.map((t, i) => {
                    const diff = t.points_for - t.points_against;
                    return (
                      <tr key={t.id} className="animate-fadein">
                        <td><span style={{ fontWeight: '700' }}>{i < 3 ? medals[i] : <span style={{ color: '#7f8c8d' }}>#{i + 1}</span>}</span></td>
                        <td>
                          <div style={{ fontWeight: '600' }}>{t.name}</div>
                          <div style={{ fontSize: '12px', color: '#7f8c8d' }}>{t.player1_name}{t.player2_name ? ` & ${t.player2_name}` : ''}</div>
                        </td>
                        <td style={{ textAlign: 'center', color: '#27ae60', fontWeight: '700' }}>{t.wins}</td>
                        <td style={{ textAlign: 'center', color: '#e74c3c', fontWeight: '700' }}>{t.losses}</td>
                        <td style={{ textAlign: 'center', fontWeight: '700', color: t.win_pct >= 60 ? '#27ae60' : '#7f8c8d' }}>{t.win_pct || 0}%</td>
                        <td style={{ textAlign: 'center', fontWeight: '600' }}>{t.points_for}</td>
                        <td style={{ textAlign: 'center', color: '#7f8c8d' }}>{t.points_against}</td>
                        <td style={{ textAlign: 'center', fontWeight: '700', color: diff >= 0 ? '#27ae60' : '#e74c3c' }}>{diff >= 0 ? '+' : ''}{diff}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {teamStandings.length === 0 && <div className="empty-state"><div className="icon">📊</div><h3>No standings data</h3><p>Play some matches to see standings</p></div>}
            </div>
          )}
          {!selectedTournament && <div className="card"><div className="empty-state"><div className="icon">🏆</div><h3>Select a tournament</h3><p>Choose a tournament above to see its team standings</p></div></div>}
        </div>
      )}
      <Footer />
    </div>
  );
}
